import { type FC, useRef } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import classnames from 'classnames';

// components
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy.ts';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';

import { PdpRespCondensedListedContent } from '../PdpRespCondensedListedContent/PdpRespCondensedListedContent.tsx';
import { usePdpRecentHistoryTracker } from '../hooks/usePdpRecentHistoryTracker.ts';
import { PdpSharedBuyerIncentivesModule } from '../shared/PdpSharedBuyerIncentivesModule/PdpSharedBuyerIncentivesModule.tsx';
import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid.tsx';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding.tsx';
import { PdpSharedSellerItemsCarousel } from '../shared/PdpSharedSellerItemsCarousel/PdpSharedSellerItemsCarousel.tsx';
import { PdpRespCondensedCircles } from '../PdpRespCondensedCircles/PdpRespCondensedCircles.tsx';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader.tsx';
import { SharedBillboardAd } from '../../shared/SharedBillboardAd/SharedBillboardAd.tsx';
import { youMayAlsoLike } from '../../shared/sharedMessages.jsx';
import { PdpSharedFeaturedRelatedItemsCarouselLazy } from '../PdpFeaturedRelatedItemsCarousel/PdpSharedFeaturedRelatedItemsCarouselLazy.tsx';

// helpers
import { getRecentlyViewedDisplayInfo } from '../helpers/recentlyViewedDisplayInfo.ts';
import { trackPdpViewMoreCtaClick } from 'dibs-recent-history/exports/recentHistoryTracking.ts';
import { locations } from '../../utils/tracking/shared/favoritesTracking.tsx';
import { useAppSelector } from '../hooks/useReactReduxHooks.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import {
    PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT,
    PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { type UserRecentHistoryItemType } from 'dibs-recent-history/exports/types.ts';
import { SIMILAR_ITEMS_LIST } from '../tracking/similarItemsTracking.js';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespCondensedContainer.scss';

import { type PdpRespCondensedContainer_viewer$data } from './__generated__/PdpRespCondensedContainer_viewer.graphql.ts';
import { type PdpRespCondensedContainer_user$data } from './__generated__/PdpRespCondensedContainer_user.graphql.ts';
import { type PdpRespCondensedContainer_item$data } from './__generated__/PdpRespCondensedContainer_item.graphql.ts';
import { type PdpRespCondensedContainer_similarLandingItems$data } from './__generated__/PdpRespCondensedContainer_similarLandingItems.graphql.ts';

type Props = {
    viewer: PdpRespCondensedContainer_viewer$data;
    user: PdpRespCondensedContainer_user$data;
    item: PdpRespCondensedContainer_item$data;
    similarLandingItems: PdpRespCondensedContainer_similarLandingItems$data;
    selectedSkuId: string;
    currency: string;
};

export const PdpRespCondensedContainerComponent: FC<Props> = ({
    currency,
    viewer,
    user,
    item,
    selectedSkuId,
    similarLandingItems,
}) => {
    const ymalScrollRef = useRef(null);
    const { isClient, isPdpCondensedSponsoredListingsTestOn } = useAppSelector(
        ({ relayVariables }) => relayVariables.variables
    );
    const { isBannerAdsAbTestVariant, isBot, pdpCondensedSponsoredListingsVariant } =
        useServerVarsContext();
    const itemId = item?.serviceId ?? '';
    const recentHistoryItems = (user?.recentHistoryItems?.edges || [])
        .map(edge => edge?.node)
        .filter(filterFalsy);
    const { showRecentlyViewed } = getRecentlyViewedDisplayInfo({
        isClient,
        itemId,
        recentHistoryItems: recentHistoryItems as UserRecentHistoryItemType[],
    });

    usePdpRecentHistoryTracker({ item });

    const similarItems =
        viewer?.similarItemsGrid?.edges?.map(edge => edge?.node).filter(filterFalsy) ?? [];

    return (
        <>
            <div className={styles.topBarWrapper}>
                {isClient && (
                    <PdpRespSimilarItemsLanding
                        scrollToRef={ymalScrollRef}
                        item={item}
                        similarItems={similarLandingItems}
                        user={user}
                        landingItemSize={item?.isSold ? undefined : 'small'}
                        currency={currency}
                    />
                )}
            </div>

            <PdpRespCondensedListedContent
                currency={currency}
                item={item}
                user={user}
                viewer={viewer}
                selectedSkuId={selectedSkuId}
            />

            {/* More from Seller */}
            <PdpSharedSellerItemsCarousel
                viewer={viewer}
                item={item}
                user={user}
                currency={currency}
                classOverrides={{
                    container: classnames(
                        dibsCss.mtSmall,
                        dibsCss.borderT,
                        dibsCss.borderTSolid,
                        dibsCss.borderTDolphin,
                        dibsCss.ptMedium,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.itemsCenter
                    ),
                    title: classnames(dibsCss.sassyFontHeaderMedium, dibsCss.m0),
                    viewAllLink: classnames(
                        dibsCss.sassyFontBody,
                        dibsCss.ptXsmall,
                        dibsCss.inlineBlock,
                        dibsCss.wFull,
                        dibsCss.textCenter
                    ),
                }}
            />

            {/* Featured Related Items */}
            {isPdpCondensedSponsoredListingsTestOn && (
                <PdpSharedFeaturedRelatedItemsCarouselLazy
                    viewer={viewer}
                    user={user}
                    currency={currency}
                    afterDisplay={isClient}
                    showLoading={pdpCondensedSponsoredListingsVariant === 'variant'}
                    classOverrides={{
                        title: classnames(
                            dibsCss.textCenter,
                            dibsCss.mxAuto,
                            dibsCss.mt0,
                            dibsCss.sassyFontHeaderMedium
                        ),
                    }}
                />
            )}

            {isBannerAdsAbTestVariant && (
                <SharedBillboardAd
                    placement={PDP_CONDENSED_TOP_BILLBOARD_PLACEMENT}
                    className={dibsCss.myLarge}
                />
            )}
            {/* Pdp Dealer Chat */}
            {isClient && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}
            {/* Circles */}
            <PdpRespCondensedCircles item={item} />
            {/* You May Also Like */}
            <div ref={ymalScrollRef}>
                <PdpRespSimilarItemsGrid
                    gridItemWrapperClass={styles.similarItems}
                    relayVariables={{ isClient }}
                    viewer={viewer}
                    currency={currency}
                    user={user}
                    item={item}
                    header={youMayAlsoLike}
                    similarItems={similarItems}
                    favoritesLocation={locations.SIMILAR_ITEM_GRID}
                    dataTn="pdp-similar-recommendation-grid"
                    gridLocation={SIMILAR_ITEMS_LIST}
                    isDark={false}
                    hideBar
                />
            </div>
            {isBannerAdsAbTestVariant && (
                <SharedBillboardAd
                    placement={PDP_CONDENSED_BOTTOM_BILLBOARD_PLACEMENT}
                    className={dibsCss.myLarge}
                />
            )}
            {isClient && showRecentlyViewed && (
                <RecentlyViewedItemsLazy
                    isBot={isBot}
                    authModalLoader={authModalLoader}
                    trackViewMoreClick={trackPdpViewMoreCtaClick}
                    hasTopSeparator
                />
            )}
            <div className={styles.buyerIncentives}>
                <PdpSharedBuyerIncentivesModule />
            </div>
        </>
    );
};

export const PdpRespCondensedContainer = createFragmentContainer(
    PdpRespCondensedContainerComponent,
    {
        viewer: graphql`
            fragment PdpRespCondensedContainer_viewer on Viewer {
                ...PdpRespSimilarItemsGrid_viewer
                ...PdpRespCondensedListedContent_viewer
                ...PdpSharedSellerItemsCarousel_viewer @include(if: $fetchPdp)
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)
                ... @include(if: $isPdpCondensedSponsoredListingsTestOn) {
                    ...PdpSharedFeaturedRelatedItemsCarouselLazy_viewer
                        @include(if: $afterDisplayAndBtNone)
                }
                similarItemsGrid: moreLikeThisItem(
                    first: 16
                    seller: EXCLUDE
                    itemId: $itemId
                    useML: true
                ) @include(if: $fetchPdp) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespCondensedContainer_user on User {
                serviceId
                ...PdpRespSimilarItemsGrid_user
                ...PdpRespSimilarItemsLanding_user
                ...PdpRespCondensedListedContent_user
                ...PdpSharedSellerItemsCarousel_user
                ...getRecentHistory_user @relay(mask: false)
                ... @include(if: $isPdpCondensedSponsoredListingsTestOn) {
                    ...PdpSharedFeaturedRelatedItemsCarouselLazy_user
                        @include(if: $afterDisplayAndBtNone)
                }
            }
        `,
        item: graphql`
            fragment PdpRespCondensedContainer_item on Item {
                serviceId
                isSold
                ...usePdpRecentHistoryTracker_item
                ...PdpRespCondensedListedContent_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpSharedSellerItemsCarousel_item
                ...PdpRespSimilarItemsGrid_item
                ...PdpRespCondensedCircles_item
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespCondensedContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems
            }
        `,
    }
);
